// src/utils/permissions.utils.ts
// Utilidades para conversión entre formatos de permisos

import { Permission, CreatePermissionDto, RoleWizardPermissions } from '../types/role.types';
import {
  SYSTEM_STRUCTURE,
  getMenuById,
  getMenuFieldFlags,
  menuBindsViewRowToMenuVisibility,
  syncBoundViewRowForMenuPermissions,
  MENU_VIEW_ROW_ACTION_ID,
} from '../config/system-structure';

/**
 * Inicializa la estructura de permisos vacía basada en SYSTEM_STRUCTURE
 */
export const initializeEmptyPermissions = (): RoleWizardPermissions => {
  const permissions: RoleWizardPermissions = {};

  SYSTEM_STRUCTURE.forEach((module) => {
    permissions[module.id] = {
      enabled: false,
      menus: {},
    };

    module.menus.forEach((menu) => {
      const menuEntry: RoleWizardPermissions[string]['menus'][string] = {
        visible: false,
        mainButtons: {},
        rowActions: {},
      };
      permissions[module.id].menus[menu.id] = menuEntry;

      // Initialize main buttons
      if (menu.mainButtons) {
        menu.mainButtons.forEach((button) => {
          permissions[module.id].menus[menu.id].mainButtons[button.id] = false;
        });
      }

      // Initialize row actions
      if (menu.rowActions) {
        menu.rowActions.forEach((action) => {
          permissions[module.id].menus[menu.id].rowActions[action.id] = false;
        });
      }

      // Initialize column flags
      if (menu.columnFlags) {
        menuEntry.columnFlags = {};
        menu.columnFlags.forEach((flag) => {
          menuEntry.columnFlags![flag.id] = false;
        });
      }

      const allFieldFlags = getMenuFieldFlags(menu);
      if (allFieldFlags.length > 0) {
        menuEntry.fieldFlags = {};
        allFieldFlags.forEach((flag) => {
          menuEntry.fieldFlags![flag.id] = false;
        });
      }

      if (menu.salesFlowActions) {
        menuEntry.salesFlowActions = {};
        menu.salesFlowActions.forEach((flow) => {
          menuEntry.salesFlowActions![flow.id] = false;
        });
      }
    });
  });

  return permissions;
};

/**
 * Convierte Permission[] (formato API) → RoleWizardPermissions (formato Wizard)
 * @param permissions Array de permisos recibidos del backend
 * @returns Estructura de permisos para el wizard
 */
// Aliases para compatibilidad con permisos antiguos o backend no normalizado
const MODULE_ALIASES: Record<string, string> = {
  crm: 'crm',
  sales: 'orders',
  ventas: 'orders',
  ordenes: 'orders',
  productos: 'products',
  inventario: 'inventory',
  reportes: 'reports',
  ajustes: 'settings',
  sistema: 'system',
};

const MENU_ALIASES: Record<string, string> = {
  roles: 'roles-management',
  'roles-settings': 'roles-management',
  users: 'users-management',
  'users-settings': 'users-management',
  catalog: 'product-catalog',
  products: 'product-catalog',
  register: 'inventory-register',
  inventory: 'inventory-register',
  query: 'inventory-warehouse',
  warehouse: 'inventory-warehouse',
  transfer: 'inventory-transfer',
  request: 'inventory-request',
  'inventory-request': 'inventory-request',
  'branch-query': 'inventory-branch',
  branch: 'inventory-branch',
  general: 'inventory-general',
  sales: 'sales-management',
  orders: 'sales-management',
  clients: 'client-management',
  'client-management': 'client-management',
  'system-parameters': 'system-config',
  system: 'system-config',
};

const ACTION_ALIASES: Record<string, string> = {
  create: 'add',
  new: 'add',
  addRole: 'add',
  update: 'edit',
  editRole: 'edit',
  remove: 'delete',
  deleteRole: 'delete',
  blockRole: 'block',
  unblockRole: 'block',
  read: 'view',
  list: 'view',
  show: 'view',
  view_row: 'view',
  /** Catálogo API usa guiones; wizard usa write_off_missing en SYSTEM_STRUCTURE */
  'write-off-missing': 'write_off_missing',
};

const normalizeText = (value: string): string => {
  return value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
};

const MODULE_NAME_TO_ID = new Map(
  SYSTEM_STRUCTURE.map((module) => [normalizeText(module.name), module.id]),
);

const MENU_NAME_TO_ID = new Map(
  SYSTEM_STRUCTURE.flatMap((module) =>
    module.menus.map((menu) => [normalizeText(menu.name), menu.id] as const),
  ),
);

const resolveModuleId = (rawModule: string): string => {
  const alias = MODULE_ALIASES[rawModule] || rawModule;
  if (SYSTEM_STRUCTURE.some((module) => module.id === alias)) return alias;
  const normalized = normalizeText(rawModule);
  return MODULE_NAME_TO_ID.get(normalized) || rawModule;
};

const resolveMenuId = (rawMenu: string): string => {
  const alias = MENU_ALIASES[rawMenu] || rawMenu;
  const hasMenuId = SYSTEM_STRUCTURE.some((module) =>
    module.menus.some((menu) => menu.id === alias),
  );
  if (hasMenuId) return alias;
  const normalized = normalizeText(rawMenu);
  return MENU_NAME_TO_ID.get(normalized) || rawMenu;
};

const actionExistsInMenu = (
  actionId: string,
  menu: {
    mainButtons: Record<string, boolean>;
    rowActions: Record<string, boolean>;
    columnFlags?: Record<string, boolean>;
    fieldFlags?: Record<string, boolean>;
    salesFlowActions?: Record<string, boolean>;
  },
): boolean =>
  menu.mainButtons[actionId] !== undefined ||
  menu.rowActions[actionId] !== undefined ||
  (menu.columnFlags != null && menu.columnFlags[actionId] !== undefined) ||
  (menu.fieldFlags != null && menu.fieldFlags[actionId] !== undefined) ||
  (menu.salesFlowActions != null && menu.salesFlowActions[actionId] !== undefined);

/**
 * Prefer an exact match on the menu before applying legacy aliases.
 * Otherwise `create` → `add` remaps inventory-general / physical-count
 * actions that are literally named `create` and they fail to reload in the wizard.
 */
const resolveActionId = (
  rawAction: string,
  menu: {
    mainButtons: Record<string, boolean>;
    rowActions: Record<string, boolean>;
    columnFlags?: Record<string, boolean>;
    fieldFlags?: Record<string, boolean>;
    salesFlowActions?: Record<string, boolean>;
  },
): string => {
  if (actionExistsInMenu(rawAction, menu)) return rawAction;
  const alias = ACTION_ALIASES[rawAction] || rawAction;
  if (alias !== rawAction && actionExistsInMenu(alias, menu)) return alias;
  return alias;
};

export const convertPermissionsFromAPI = (permissions: Permission[]): RoleWizardPermissions => {
  // Inicializar estructura vacía
  const wizardPermissions = JSON.parse(JSON.stringify(initializeEmptyPermissions()));

  // Si no hay permisos, retornar estructura vacía
  if (!permissions || permissions.length === 0) {
    return wizardPermissions;
  }

  // Procesar cada permiso del API
  permissions.forEach((perm) => {
    // Normalizar menu y action con alias conocidos
    const normalizedModule = resolveModuleId(perm.module);
    const normalizedMenu = resolveMenuId(perm.menu);

    const module = wizardPermissions[normalizedModule];
    if (!module) {
      console.warn(
        `Module "${normalizedModule}" not found in SYSTEM_STRUCTURE (original: ${perm.module})`,
      );
      return;
    }

    module.enabled = true;

    const menu = module.menus[normalizedMenu];
    if (!menu) {
      console.warn(
        `Menu "${normalizedMenu}" not found in module "${normalizedModule}" (original: ${perm.menu})`,
      );
      return;
    }

    // Legacy SOL: create permission was stored under inventory-request
    if (
      normalizedModule === 'inventory' &&
      normalizedMenu === 'inventory-request' &&
      perm.action === 'create'
    ) {
      const generalMenu = module.menus['inventory-general'];
      if (generalMenu?.salesFlowActions?.create !== undefined) {
        generalMenu.salesFlowActions.create = true;
      }
      return;
    }

    // Legacy SOL: view_row collapsed into menu visibility (view)
    if (
      normalizedModule === 'inventory' &&
      normalizedMenu === 'inventory-request' &&
      perm.action === 'view_row'
    ) {
      menu.visible = true;
      return;
    }

    // Legacy SOL: approve was stored with resource flow
    if (
      normalizedModule === 'inventory' &&
      normalizedMenu === 'inventory-request' &&
      perm.action === 'approve' &&
      perm.resource === 'flow'
    ) {
      if (menu.rowActions.approve !== undefined) {
        menu.rowActions.approve = true;
        menu.visible = true;
      }
      return;
    }

    const normalizedAction = resolveActionId(perm.action, menu);

    // Normalizar resource: el backend a veces no envía resource
    const resource = perm.resource;
    const knownResources = new Set(['menu', 'button', 'action', 'flag', 'field', 'flow']);

    // Visibilidad de menú: resource 'menu' o action 'view' sin resource
    if (resource === 'menu' || (!resource && normalizedAction === 'view')) {
      menu.visible = true;
      return;
    }

    // Botones principales: resource 'button' o coincidir con mainButtons si no hay resource
    const isMainButton = menu.mainButtons && menu.mainButtons[normalizedAction] !== undefined;
    if (resource === 'button' || (!resource && isMainButton)) {
      if (isMainButton) {
        menu.mainButtons[normalizedAction] = true;
        menu.visible = true;
      } else {
        console.warn(
          `Main button "${normalizedAction}" not found in menu "${normalizedMenu}" of module "${normalizedModule}"`,
        );
      }
      return;
    }

    // Acciones de fila: resource 'action' o coincidir con rowActions si no hay resource
    const isRowAction = menu.rowActions && menu.rowActions[normalizedAction] !== undefined;
    if (resource === 'action' || (!resource && isRowAction)) {
      if (isRowAction) {
        menu.rowActions[normalizedAction] = true;
        menu.visible = true;
      } else {
        console.warn(
          `Row action "${normalizedAction}" not found in menu "${normalizedMenu}" of module "${normalizedModule}"`,
        );
      }
      return;
    }

    // Flags de columna: resource 'flag' o coincidir con columnFlags si no hay resource
    const hasFlag = menu.columnFlags && menu.columnFlags[normalizedAction] !== undefined;
    if (resource === 'flag' || (!resource && hasFlag)) {
      if (menu.columnFlags && hasFlag) {
        menu.columnFlags[normalizedAction] = true;
        menu.visible = true;
      } else {
        console.warn(
          `Column flag "${normalizedAction}" not found in menu "${normalizedMenu}" of module "${normalizedModule}"`,
        );
      }
      return;
    }

    // Campos visibles: resource 'field'
    const hasFieldFlag = menu.fieldFlags && menu.fieldFlags[normalizedAction] !== undefined;
    if (resource === 'field' || (!resource && hasFieldFlag)) {
      if (menu.fieldFlags && hasFieldFlag) {
        menu.fieldFlags[normalizedAction] = true;
        menu.visible = true;
      } else {
        console.warn(
          `Field flag "${normalizedAction}" not found in menu "${normalizedMenu}" of module "${normalizedModule}"`,
        );
      }
      return;
    }

    // Flujo de venta / similares: resource 'flow'
    const hasSalesFlow =
      menu.salesFlowActions != null &&
      Object.prototype.hasOwnProperty.call(menu.salesFlowActions, normalizedAction);
    if (resource === 'flow' || (!resource && hasSalesFlow)) {
      if (menu.salesFlowActions?.[normalizedAction] !== undefined) {
        menu.salesFlowActions[normalizedAction] = true;
        menu.visible = true;
      } else {
        console.warn(
          `Sales flow action "${normalizedAction}" not found in menu "${normalizedMenu}" of module "${normalizedModule}"`,
        );
      }
      return;
    }

    if (resource && !knownResources.has(resource)) {
      if (normalizedAction === 'view') {
        menu.visible = true;
        return;
      }
      if (isMainButton) {
        menu.mainButtons[normalizedAction] = true;
        menu.visible = true;
        return;
      }
      if (isRowAction) {
        menu.rowActions[normalizedAction] = true;
        menu.visible = true;
        return;
      }
      if (menu.columnFlags && hasFlag) {
        menu.columnFlags[normalizedAction] = true;
        menu.visible = true;
        return;
      }
      if (menu.fieldFlags && hasFieldFlag) {
        menu.fieldFlags[normalizedAction] = true;
        menu.visible = true;
        return;
      }
      const sf =
        menu.salesFlowActions &&
        Object.prototype.hasOwnProperty.call(menu.salesFlowActions, normalizedAction);
      if (menu.salesFlowActions && sf) {
        menu.salesFlowActions[normalizedAction] = true;
        menu.visible = true;
        return;
      }
    }

    // Fallback: no se pudo mapear
    console.warn(
      `Permission "${normalizedAction}" (${resource || 'no resource'}) not mapped for menu "${normalizedMenu}" module "${normalizedModule}"`,
    );
  });

  Object.keys(wizardPermissions).forEach((moduleId) => {
    const module = wizardPermissions[moduleId];
    if (!module) return;
    Object.keys(module.menus).forEach((menuId) => {
      const menu = module.menus[menuId];
      if (menu) {
        syncBoundViewRowForMenuPermissions(menuId, menu);
      }
    });
  });

  return wizardPermissions;
};

/**
 * Genera todos los permisos para un menú con granularidad 'full'
 * @param moduleId ID del módulo
 * @param menuId ID del menú
 * @param menuConfig Configuración del menú desde SYSTEM_STRUCTURE
 * @returns Array de permisos generados
 */
const generateFullMenuPermissions = (
  moduleId: string,
  menuId: string,
  menuConfig: { mainButtons?: Array<{ id: string }>; rowActions?: Array<{ id: string }> },
): CreatePermissionDto[] => {
  const permissions: CreatePermissionDto[] = [];

  // Permiso de visibilidad del menú
  permissions.push({
    module: moduleId,
    menu: menuId,
    action: 'view',
    resource: 'menu',
  });

  // Todos los botones principales
  if (menuConfig.mainButtons) {
    menuConfig.mainButtons.forEach((button) => {
      permissions.push({
        module: moduleId,
        menu: menuId,
        action: button.id,
        resource: 'button',
      });
    });
  }

  // Todas las acciones de fila
  if (menuConfig.rowActions) {
    menuConfig.rowActions.forEach((action) => {
      permissions.push({
        module: moduleId,
        menu: menuId,
        action: action.id === 'view' ? 'view_row' : action.id,
        resource: 'action',
      });
    });
  }

  return permissions;
};

/**
 * Convierte RoleWizardPermissions (formato Wizard) → CreatePermissionDto[] (formato API)
 * Usa la configuración de granularidad de SYSTEM_STRUCTURE para determinar cómo procesar cada menú
 * @param wizardPermissions Estructura de permisos del wizard
 * @returns Array de permisos para enviar al backend (sin id, el backend lo genera)
 */
export const convertPermissionsToAPI = (
  wizardPermissions: RoleWizardPermissions,
): CreatePermissionDto[] => {
  const permissions: CreatePermissionDto[] = [];

  Object.entries(wizardPermissions).forEach(([moduleId, module]) => {
    if (!module.enabled) return;

    Object.entries(module.menus).forEach(([menuId, menu]) => {
      // Obtener configuración del menú desde SYSTEM_STRUCTURE
      const menuConfig = getMenuById(menuId);
      const granularity = menuConfig?.granularity || 'custom';

      // Manejar diferentes tipos de granularidad
      if (granularity === 'full' && menu.visible) {
        // Generar todos los permisos automáticamente
        if (menuConfig) {
          permissions.push(...generateFullMenuPermissions(moduleId, menuId, menuConfig));
        }
        return; // Saltar el procesamiento granular para este menú
      }

      if (granularity === 'menu-only' && menu.visible) {
        // Solo permiso de visibilidad del menú
        permissions.push({
          module: moduleId,
          menu: menuId,
          action: 'view',
          resource: 'menu',
        });
        return; // Saltar el procesamiento granular para este menú
      }

      // Granularidad 'custom': procesar individualmente cada permiso
      const rowActionsForApi = { ...menu.rowActions };
      if (menu.visible && menuBindsViewRowToMenuVisibility(menuId)) {
        if (Object.prototype.hasOwnProperty.call(rowActionsForApi, MENU_VIEW_ROW_ACTION_ID)) {
          rowActionsForApi[MENU_VIEW_ROW_ACTION_ID] = true;
        }
      }

      const isConfigOnly = Boolean(menuConfig?.configOnly);

      // Menu visibility permission (not for config-only; those have no sidebar entry)
      if (menu.visible && !isConfigOnly) {
        permissions.push({
          module: moduleId,
          menu: menuId,
          action: 'view',
          resource: 'menu',
        });
      }

      // Main buttons permissions
      Object.entries(menu.mainButtons).forEach(([buttonId, enabled]) => {
        if (enabled) {
          permissions.push({
            module: moduleId,
            menu: menuId,
            action: buttonId,
            resource: 'button',
          });
        }
      });

      // Row actions permissions
      Object.entries(rowActionsForApi).forEach(([actionId, enabled]) => {
        if (enabled) {
          permissions.push({
            module: moduleId,
            menu: menuId,
            action: actionId === MENU_VIEW_ROW_ACTION_ID ? 'view_row' : actionId,
            resource: 'action',
          });
        }
      });

      // Column flags permissions
      if (menu.columnFlags) {
        Object.entries(menu.columnFlags).forEach(([flagId, enabled]) => {
          if (enabled) {
            permissions.push({
              module: moduleId,
              menu: menuId,
              action: flagId,
              resource: 'flag',
            });
          }
        });
      }

      if (menu.fieldFlags) {
        Object.entries(menu.fieldFlags).forEach(([flagId, enabled]) => {
          if (enabled) {
            permissions.push({
              module: moduleId,
              menu: menuId,
              action: flagId,
              resource: 'field',
            });
          }
        });
      }

      if (menu.salesFlowActions) {
        Object.entries(menu.salesFlowActions).forEach(([flowId, enabled]) => {
          if (enabled) {
            const catalogAction =
              menuId === 'physical-count' && flowId === 'write_off_missing'
                ? 'write-off-missing'
                : flowId;
            permissions.push({
              module: moduleId,
              menu: menuId,
              action: catalogAction,
              resource: 'flow',
            });
          }
        });
      }
    });
  });

  return permissions;
};
